import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BookOpen, CheckCircle2, Lock, PlayCircle } from "lucide-react";

const statusStyle = {
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Not Started": "bg-sky-50 text-sky-700 border-sky-200",
  Locked: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function StudentHome() {
  const { auth } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState(null);
  const [chapters, setChapters] = useState([]);

  useEffect(() => { api.get("/student/subjects").then((r) => setSubjects(r.data)); }, []);

  const openSubject = async (s) => {
    setSubject(s);
    const { data } = await api.get("/student/chapters", { params: { subject_id: s.id } });
    setChapters(data);
  };

  return (
    <Shell title={`Hi, ${auth?.student?.name?.split(" ")[0] || "Student"}!`}
      subtitle={`Roll ${auth?.student?.roll_no} · Division ${auth?.student?.division}`}>
      {!subject ? (
        <>
          <h2 className="text-2xl sm:text-3xl font-head font-bold">Pick a subject</h2>
          <p className="text-slate-600 mt-2">Choose a subject to see your chapter tests.</p>
          {subjects.length === 0 && <p className="text-slate-500 mt-8" data-testid="no-subjects">No subjects assigned to your class yet.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="subject-cards">
            {subjects.map((s, i) => (
              <button key={s.id} onClick={() => openSubject(s)} className="soft-card soft-card-hover rounded-3xl p-8 text-left"
                data-testid={`subject-card-${s.name.toLowerCase()}`}>
                <div className="h-12 w-12 rounded-2xl flex items-center justify-center"
                  style={{ background: ["#BAE6FD", "#FBCFE8", "#E9D5FF", "#FEF08A"][i % 4] }}>
                  <BookOpen className="h-6 w-6 text-slate-800" />
                </div>
                <h3 className="text-xl font-head font-bold mt-5">{s.name}</h3>
                <p className="text-sm text-slate-500 mt-1">View chapter tests</p>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-head font-bold">{subject.name}</h2>
            <Button variant="ghost" onClick={() => setSubject(null)} data-testid="back-to-subjects">Change subject</Button>
          </div>
          {chapters.length === 0 && <p className="text-slate-500 mt-8" data-testid="no-chapters">No chapters available yet.</p>}
          <div className="space-y-6 mt-8" data-testid="chapters-list">
            {chapters.map((ch) => (
              <div key={ch.id} className="soft-card rounded-3xl p-6" data-testid={`chapter-block-${ch.id}`}>
                <h3 className="text-lg font-head font-bold">{ch.name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {ch.tests.length === 0 && <p className="text-sm text-slate-500">No tests in this chapter yet</p>}
                  {ch.tests.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-2xl p-4" data-testid={`test-row-${t.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{t.title}</p>
                        <p className="text-xs text-slate-500">{t.question_count} questions</p>
                        <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full border ${statusStyle[t.status]}`} data-testid={`test-status-${t.id}`}>
                          {t.status}{t.status === "Completed" ? ` · ${t.percentage}%` : ""}
                        </span>
                      </div>
                      {t.status === "Completed" ? (
                        <Link to={`/result/${t.attempt_id}`}>
                          <Button size="sm" variant="outline" className="rounded-full" data-testid={`view-result-btn-${t.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Result
                          </Button>
                        </Link>
                      ) : t.status === "Locked" ? (
                        <Button size="sm" variant="ghost" disabled data-testid={`locked-btn-${t.id}`}><Lock className="h-4 w-4" /></Button>
                      ) : (
                        <Link to={`/student/test/${t.id}`}>
                          <Button size="sm" className="rounded-full" data-testid={`start-test-btn-${t.id}`}>
                            <PlayCircle className="h-4 w-4 mr-1" />Start
                          </Button>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Shell>
  );
}
